import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Form, Button, Card, Spinner, Alert, Dropdown, Modal } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, onValue, set, off, update, remove } from '@firebase/database';
import { FaPaperPlane, FaArrowLeft, FaCheck, FaCheckDouble, FaEllipsisV } from 'react-icons/fa';
import { toast } from 'react-toastify';
import './Chat.css';
import BackToHome from '../components/BackToHome';
import { sanitizeInput } from '../utils/security';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

const Chat = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatInfo, setChatInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typing, setTyping] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeout = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Typing indicator logic
  useEffect(() => {
    if (!currentUser || !chatId) return;
    const typingRef = ref(database, `chats/${chatId}/typing`);
    const listener = onValue(typingRef, (snapshot) => {
      const typingData = snapshot.val();
      if (typingData && typingData !== currentUser.uid) {
        setIsTyping(true);
      } else {
        setIsTyping(false);
      }
    });
    return () => off(typingRef, listener);
  }, [chatId, currentUser]);

  // Mark messages as read when chat is opened or messages change
  useEffect(() => {
    if (!currentUser || !chatId || !messages.length) return;
    const unreadMessages = messages.filter(
      (msg) => msg.senderId !== currentUser.uid && !msg.read
    );
    if (unreadMessages.length > 0) {
      const updates = {};
      unreadMessages.forEach((msg) => {
        updates[`chats/${chatId}/messages/${msg.id}/read`] = true;
      });
      update(ref(database, '/'), updates);
    }
  }, [messages, chatId, currentUser]);

  useEffect(() => {
    if (!currentUser || !chatId) {
      setError('Please login to access chat');
      setLoading(false);
      return;
    }

    let isMounted = true;
    const chatRef = ref(database, `chats/${chatId}`);
    const unsubscribe = onValue(
      chatRef,
      (snapshot) => {
        if (!isMounted) return;
        if (!snapshot.exists()) {
          setError('Chat not found');
          setLoading(false);
          return;
        }
        const chatData = snapshot.val();
        if (!chatData.participants?.[currentUser.uid]) {
          setError('You are not authorized to view this chat');
          setLoading(false);
          return;
        }
        setChatInfo(chatData);
        if (chatData.messages) {
          const messagesList = Object.entries(chatData.messages)
            .map(([key, value]) => ({
              id: key,
              ...value
            }))
            .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
          setMessages(messagesList);
          setTimeout(scrollToBottom, 100);
        }
        setLoading(false);
      },
      (error) => {
        setError('Failed to load chat');
        setLoading(false);
      }
    );
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [chatId, currentUser]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    try {
      // Sanitize message to prevent XSS attacks
      const sanitizedMessage = sanitizeInput(newMessage.trim());
      
      if (!sanitizedMessage || sanitizedMessage.length === 0) {
        toast.error('Message cannot be empty');
        return;
      }
      
      // Limit message length (prevent DoS)
      if (sanitizedMessage.length > 500) {
        toast.error('Message is too long (max 500 characters)');
        return;
      }
      
      const messageData = {
        text: sanitizedMessage,
        senderId: currentUser.uid,
        senderName: currentUser.displayName || currentUser.email,
        timestamp: Date.now(),
        read: false
      };
      const messageId = `msg_${Date.now()}`;
      const otherParticipant = Object.values(chatInfo.participants).find(
        p => p.id !== currentUser.uid
      );
      const recipientId = otherParticipant?.id;
      const recipientName = typeof otherParticipant?.name === 'string'
        ? otherParticipant.name
        : 'Unknown';
      const senderName = typeof chatInfo.participants?.[currentUser.uid]?.name === 'string'
        ? chatInfo.participants[currentUser.uid].name
        : (currentUser.displayName || currentUser.email);
      const adTitle = typeof chatInfo.adTitle === 'string' ? chatInfo.adTitle : '';
      const updates = {
        [`chats/${chatId}/messages/${messageId}`]: messageData,
        [`chats/${chatId}/lastMessage`]: sanitizedMessage,
        [`chats/${chatId}/lastMessageTime`]: Date.now(),
        [`user_chats/${currentUser.uid}/${chatId}`]: {
          adTitle,
          otherUserName: recipientName,
          lastMessage: sanitizedMessage,
          timestamp: Date.now(),
          unreadCount: 0
        },
      };
      if (recipientId) {
        updates[`user_chats/${recipientId}/${chatId}`] = {
          adTitle,
          otherUserName: senderName,
          lastMessage: sanitizedMessage,
          timestamp: Date.now(),
          unreadCount: 1
        };
      }
      // Match Firebase reserved characters: . # $ [ ]
      // eslint-disable-next-line no-useless-escape
      const reservedCharPattern = /[.#$\[\]]/;
      Object.keys(updates).forEach(key => {
        if (reservedCharPattern.test(key)) {
          throw new Error(`Invalid Firebase key: ${key}`);
        }
      });
      for (const [key, value] of Object.entries(updates)) {
        try {
          await update(ref(database, '/'), { [key]: value });
        } catch (err) {
          console.error(`Update failed for path: ${key}`, err);
        }
      }
      setNewMessage(''); // Clear input after sending
      scrollToBottom();
      // Remove typing status after sending
      setTyping(false);
      await set(ref(database, `chats/${chatId}/typing`), '');
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  // Typing status update
  const handleTyping = (e) => {
    // Limit input to 500 characters
    const value = e.target.value;
    if (value.length <= 500) {
      setNewMessage(value);
    }
    if (!typing) {
      setTyping(true);
      set(ref(database, `chats/${chatId}/typing`), currentUser.uid);
    }
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      setTyping(false);
      set(ref(database, `chats/${chatId}/typing`), '');
    }, 1500);
  };

  const handleBack = () => {
    navigate('/chats');
  };

  // Block user handler
  const handleBlock = () => {
    setShowMenu(false);
    toast.info('User blocked (demo only).');
    // Implement actual block logic as needed
  };

  // Report user handler
  const handleReport = () => {
    setShowMenu(false);
    toast.info('User reported (demo only).');
    // Implement actual report logic as needed
  };

  // Delete chat handler
  const handleDelete = async () => {
    setShowDeleteModal(false);
    try {
      // Remove chat for current user only
      await remove(ref(database, `user_chats/${currentUser.uid}/${chatId}`));
      toast.success('Chat deleted.');
      navigate('/chats');
    } catch (err) {
      toast.error('Failed to delete chat.');
    }
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status" size="lg">
          <span className="visually-hidden">Loading chat...</span>
        </Spinner>
        <p className="mt-3">Loading chat...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <BackToHome />
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
        <Button variant="outline-secondary" onClick={handleBack}>
          <FaArrowLeft className="me-2" /> Back to Chats
        </Button>
      </Container>
    );
  }

  if (!chatInfo) {
    return (
      <Container className="mt-4">
        <BackToHome />
        <Alert variant="warning">
          <Alert.Heading>Chat Not Found</Alert.Heading>
          <p>The chat you're looking for doesn't exist or has been deleted.</p>
        </Alert>
        <Button variant="outline-secondary" onClick={handleBack}>
          <FaArrowLeft className="me-2" /> Back to Chats
        </Button>
      </Container>
    );
  }

  const otherParticipant = Object.values(chatInfo.participants).find(
    p => p.id !== currentUser.uid
  );

  return (
    <Container className="chat-container">
      <BackToHome />
      <Card className="chat-card">
        <Card.Header className="chat-header">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <Button 
                variant="link" 
                onClick={handleBack} 
                className="chat-back-btn"
              >
                <FaArrowLeft />
              </Button>
              <div className="chat-header-info">
                <h5 className="mb-0 chat-header-title">{chatInfo.adTitle}</h5>
                <small className="chat-header-subtitle">
                  {otherParticipant?.name || 'Unknown User'}
                </small>
              </div>
            </div>
            <Dropdown show={showMenu} onToggle={() => setShowMenu(!showMenu)} align="end">
              <Dropdown.Toggle as="span" className="chat-menu-toggle">
                <FaEllipsisV size={18} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleBlock}>Block User</Dropdown.Item>
                <Dropdown.Item onClick={handleReport}>Report User</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => setShowDeleteModal(true)} className="text-danger">Delete Chat</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Card.Header>

        <Card.Body className="chat-messages">
          {messages.length === 0 ? (
            <div className="chat-empty-state">
              <div className="chat-empty-icon">💬</div>
              <p className="chat-empty-text">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            <>
              {messages.map((message) => {
                const isSent = message.senderId === currentUser.uid;
                const initials = getInitials(message.senderName);
                return (
                  <div
                    key={message.id}
                    className={`message-wrapper ${isSent ? 'sent' : 'received'}`}
                  >
                    {!isSent && (
                      <div className="message-avatar">{initials}</div>
                    )}
                    <div className={`message-bubble ${isSent ? 'sent' : 'received'}`}>
                      <div className="message-text">{message.text}</div>
                      <div className="message-footer">
                        <span className="message-time">
                          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {isSent && (
                          <span className="message-status">
                            {message.read ? (
                              <FaCheckDouble className="message-read" title="Read" />
                            ) : (
                              <FaCheck className="message-sent" title="Sent" />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                    {isSent && (
                      <div className="message-avatar sent-avatar">{getInitials(currentUser.displayName || currentUser.email)}</div>
                    )}
                  </div>
                );
              })}
              {isTyping && (
                <div className="message-wrapper received">
                  <div className="message-avatar">{getInitials(otherParticipant?.name)}</div>
                  <div className="message-bubble received typing-bubble">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </Card.Body>

        <Card.Footer className="chat-footer">
          <Form onSubmit={handleSendMessage} autoComplete="off" className="chat-input-form">
            <div className="chat-input-wrapper">
              <Form.Control
                type="text"
                value={newMessage}
                onChange={handleTyping}
                placeholder="Type your message..."
                className="chat-input"
                autoComplete="off"
                maxLength={500}
              />
              <Button 
                type="submit" 
                variant="primary" 
                className="chat-send-btn"
                disabled={!newMessage.trim()}
              >
                <FaPaperPlane />
              </Button>
            </div>
          </Form>
        </Card.Footer>
      </Card>
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Chat</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this chat? This cannot be undone.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Chat; 